import React, { useState, useEffect } from 'react';
import {
    CreditCard, Plus, Edit, Trash2, Settings, Users, Search,
    Filter, Save, X, Check, AlertCircle, Eye, DollarSign,
    Calendar, Activity, CheckCircle, Clock, ToggleLeft, ToggleRight,
    Crown, Music, Star, ArrowRight
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const fmtCurrency = (cents, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format((cents || 0) / 100);

const PlanPreviewCard = ({ formData, featuresText }) => {
    const features = featuresText.split('\n').map(f => f.trim()).filter(Boolean);
    const isYearly = formData.billing_interval === 'YEARLY';
    const badgeLabel = formData.badge_text || null;
    const price = parseInt(formData.price, 10) || 0;

    return (
        <div className={`relative overflow-hidden rounded-xl p-6 ${
            isYearly
                ? 'border border-purple-500/50 bg-gradient-to-br from-purple-900/20 to-gray-800/80'
                : 'border border-blue-500/30 bg-gray-800/60'
        }`}>
            {badgeLabel && (
                <div className="absolute top-4 right-4">
                    <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        {badgeLabel}
                    </span>
                </div>
            )}
            <div className="text-center mb-5">
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-3 ${
                    isYearly ? 'bg-purple-600' : 'bg-blue-600'
                }`}>
                    {isYearly ? <Crown className="w-7 h-7 text-white" /> : <Music className="w-7 h-7 text-white" />}
                </div>
                <h3 className="text-xl font-bold text-white mb-1">
                    {formData.name || <span className="text-gray-500 italic">Plan name</span>}
                </h3>
                <p className="text-gray-400 text-sm">
                    {formData.description || <span className="italic">No description</span>}
                </p>
            </div>

            <div className="text-center mb-5">
                <div className="flex items-baseline justify-center gap-1">
                    <span className="text-3xl font-bold text-white">{fmtCurrency(price)}</span>
                    <span className="text-gray-400 text-sm">
                        /{isYearly ? 'year' : 'month'}
                    </span>
                </div>
            </div>

            <div className="space-y-2 mb-6">
                {features.length > 0 ? features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{f}</span>
                    </div>
                )) : (
                    <p className="text-gray-600 text-sm italic text-center">No features yet</p>
                )}
            </div>

            <div className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 ${
                isYearly
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'bg-blue-600 text-white'
            }`}>
                Get Started
                <ArrowRight className="w-4 h-4" />
            </div>
        </div>
    );
};

const PaymentsManagement = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [environment, setEnvironment] = useState('sandbox'); // sandbox or production
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [stats, setStats] = useState(null);
    
    // Modal states
    const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
    const [showEditPlanModal, setShowEditPlanModal] = useState(false);
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [showSubscriberModal, setShowSubscriberModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [transactionDetails, setTransactionDetails] = useState(null);
    const [loadingTransactionDetails, setLoadingTransactionDetails] = useState(false);
    const [selectedSubscriber, setSelectedSubscriber] = useState(null);
    
    // Form data for subscription plan
    const [planFormData, setPlanFormData] = useState({
        name: '',
        description: '',
        price: '',
        billing_interval: 'MONTHLY',
        currency: 'USD',
        features: [],
        is_active: true,
        is_popular: false,
        badge_text: '',
        sort_order: 0
    });

    // features as newline-separated text for textarea editing
    const [featuresText, setFeaturesText] = useState('');

    useEffect(() => {
        fetchDashboardData();
    }, [environment]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            await Promise.all([
                fetchStats(),
                fetchSubscriptionPlans(),
                fetchTransactions(),
                fetchSubscribers()
            ]);
        } catch (error) {
            console.error('Error fetching payments data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.get(`/payments/stats?environment=${environment}`);
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching payment stats:', error);
        }
    };

    const fetchSubscriptionPlans = async () => {
        try {
            const response = await api.get(`/payments/plans?environment=${environment}`);
            if (response.data.success) {
                setSubscriptionPlans(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching subscription plans:', error);
        }
    };

    const fetchTransactions = async () => {
        try {
            const response = await api.get(`/payments/transactions?environment=${environment}&limit=50`);
            if (response.data.success) {
                setTransactions(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
        }
    };

    const fetchSubscribers = async () => {
        try {
            const response = await api.get(`/payments/subscribers?environment=${environment}&limit=50`);
            if (response.data.success) {
                setSubscribers(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching subscribers:', error);
        }
    };

    const emptyPlanForm = {
        name: '',
        description: '',
        price: '',
        billing_interval: 'MONTHLY',
        currency: 'USD',
        features: [],
        is_active: true,
        is_popular: false,
        badge_text: '',
        sort_order: 0
    };

    const handleCreatePlan = async (e) => {
        e.preventDefault();
        try {
            const features = featuresText.split('\n').map(f => f.trim()).filter(Boolean);
            const response = await api.post(`/payments/plans?environment=${environment}`, {
                ...planFormData,
                price: parseInt(planFormData.price, 10),
                features,
                sort_order: parseInt(planFormData.sort_order, 10) || 0
            });

            if (response.data.success) {
                await fetchSubscriptionPlans();
                setShowCreatePlanModal(false);
                setPlanFormData(emptyPlanForm);
                setFeaturesText('');
            }
        } catch (error) {
            console.error('Error creating subscription plan:', error);
        }
    };

    const handleEditPlan = async (e) => {
        e.preventDefault();
        if (!selectedPlan) return;

        try {
            const features = featuresText.split('\n').map(f => f.trim()).filter(Boolean);
            const response = await api.put(`/payments/plans/${selectedPlan.id}?environment=${environment}`, {
                ...planFormData,
                price: parseInt(planFormData.price, 10),
                features,
                sort_order: parseInt(planFormData.sort_order, 10) || 0
            });

            if (response.data.success) {
                await fetchSubscriptionPlans();
                setShowEditPlanModal(false);
                setSelectedPlan(null);
            }
        } catch (error) {
            console.error('Error updating subscription plan:', error);
        }
    };

    const handleTogglePlan = async (plan) => {
        try {
            const response = await api.patch(`/payments/plans/${plan.id}/toggle?environment=${environment}`);
            if (response.data.success) {
                await fetchSubscriptionPlans();
            }
        } catch (error) {
            console.error('Error toggling plan status:', error);
        }
    };

    const handleViewTransactionDetails = async (transaction) => {
        setSelectedTransaction(transaction);
        setShowTransactionModal(true);
        setLoadingTransactionDetails(true);
        
        try {
            const response = await api.get(`/payments/transactions/${transaction.id}?environment=${environment}`);
            if (response.data.success) {
                setTransactionDetails(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching transaction details:', error);
            setTransactionDetails(null);
        } finally {
            setLoadingTransactionDetails(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount / 100); // Assuming amounts are in cents
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-3 animate-pulse" />
                        <p className="text-gray-400">Loading payments dashboard...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <CreditCard className="w-8 h-8 text-green-400" />
                        <h1 className="text-4xl font-black gradient-text">Payments Management</h1>
                    </div>
                    <p className="text-gray-400">
                        Manage Square payment integration and subscription plans
                    </p>
                </div>

                {/* Environment Toggle */}
                <div className="mb-8">
                    <div className="glass-card p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-gray-300 font-medium">Environment:</span>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setEnvironment('sandbox')}
                                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                            environment === 'sandbox' 
                                                ? 'bg-yellow-600 text-white' 
                                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        }`}
                                    >
                                        Sandbox
                                    </button>
                                    <button
                                        onClick={() => setEnvironment('production')}
                                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                            environment === 'production' 
                                                ? 'bg-green-600 text-white' 
                                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        }`}
                                    >
                                        Production
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-400">
                                    {environment === 'sandbox' ? '⚠️ Test Mode' : '🚨 Live Mode'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-8">
                    <div className="flex space-x-1 bg-gray-800 rounded-lg p-1">
                        {[
                            { id: 'overview', label: 'Overview', icon: Activity },
                            { id: 'plans', label: 'Subscription Plans', icon: CreditCard },
                            { id: 'transactions', label: 'Transactions', icon: DollarSign },
                            { id: 'subscribers', label: 'Subscribers', icon: Users }
                        ].map((tab) => {
                            const IconComponent = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                                        activeTab === tab.id
                                            ? 'bg-blue-600 text-white'
                                            : 'text-gray-400 hover:text-white hover:bg-gray-700'
                                    }`}
                                >
                                    <IconComponent className="w-4 h-4" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="glass-card p-6 border border-green-500/30">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-green-600 rounded-lg">
                                        <DollarSign className="w-6 h-6 text-white" />
                                    </div>
                                    <Activity className="w-5 h-5 text-green-400" />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-1">
                                    {formatCurrency(stats?.total_revenue || 0)}
                                </h3>
                                <p className="text-gray-400 text-sm">Total Revenue</p>
                            </div>

                            <div className="glass-card p-6 border border-blue-500/30">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-blue-600 rounded-lg">
                                        <Users className="w-6 h-6 text-white" />
                                    </div>
                                    <Activity className="w-5 h-5 text-blue-400" />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-1">
                                    {stats?.active_subscribers || 0}
                                </h3>
                                <p className="text-gray-400 text-sm">Active Subscribers</p>
                            </div>

                            <div className="glass-card p-6 border border-purple-500/30">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-purple-600 rounded-lg">
                                        <CreditCard className="w-6 h-6 text-white" />
                                    </div>
                                    <Activity className="w-5 h-5 text-purple-400" />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-1">
                                    {stats?.total_transactions || 0}
                                </h3>
                                <p className="text-gray-400 text-sm">Total Transactions</p>
                            </div>

                            <div className="glass-card p-6 border border-yellow-500/30">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-yellow-600 rounded-lg">
                                        <CheckCircle className="w-6 h-6 text-white" />
                                    </div>
                                    <Activity className="w-5 h-5 text-yellow-400" />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-1">
                                    {stats?.success_rate || 0}%
                                </h3>
                                <p className="text-gray-400 text-sm">Success Rate</p>
                            </div>
                        </div>

                        {/* System Status */}
                        <div className="glass-card p-6">
                            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                                <Settings className="w-5 h-5 text-green-400" />
                                Square Integration Status
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex items-center gap-3 p-3 bg-green-600/20 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                    <div>
                                        <p className="text-green-400 font-semibold">API Connection</p>
                                        <p className="text-gray-300 text-sm">Connected to Square API</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-3 p-3 bg-green-600/20 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                    <div>
                                        <p className="text-green-400 font-semibold">Webhooks</p>
                                        <p className="text-gray-300 text-sm">Event notifications active</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-3 p-3 bg-green-600/20 rounded-lg">
                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                    <div>
                                        <p className="text-green-400 font-semibold">Environment</p>
                                        <p className="text-gray-300 text-sm capitalize">{environment} mode</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Subscription Plans Tab */}
                {activeTab === 'plans' && (
                    <div className="space-y-6">
                        {/* Header with Create Button */}
                        <div className="flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-white">Subscription Plans</h2>
                            <button
                                onClick={() => setShowCreatePlanModal(true)}
                                className="btn-primary flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Create Plan
                            </button>
                        </div>

                        {/* Plans Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {subscriptionPlans.map((plan) => (
                                <div key={plan.id} className="glass-card p-6 border border-blue-500/30">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                                            <p className="text-gray-400 text-sm">{plan.description}</p>
                                        </div>
                                        <button
                                            onClick={() => handleTogglePlan(plan)}
                                            className="flex items-center"
                                        >
                                            {plan.is_active ? (
                                                <ToggleRight className="w-6 h-6 text-green-400" />
                                            ) : (
                                                <ToggleLeft className="w-6 h-6 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                    
                                    <div className="mb-2">
                                        <span className="text-3xl font-bold text-white">
                                            {formatCurrency(plan.price, plan.currency)}
                                        </span>
                                        <span className="text-gray-400">/{plan.billing_interval}</span>
                                    </div>
                                    {plan.badge_text && (
                                        <span className="inline-block mb-3 px-2 py-0.5 bg-purple-600/30 text-purple-300 text-xs rounded-full">
                                            {plan.badge_text}
                                        </span>
                                    )}
                                    {Array.isArray(plan.features) && plan.features.length > 0 && (
                                        <p className="text-xs text-gray-500 mb-3">{plan.features.length} features</p>
                                    )}

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setSelectedPlan(plan);
                                                setPlanFormData({
                                                    ...emptyPlanForm,
                                                    ...plan,
                                                    price: plan.price ?? '',
                                                    sort_order: plan.sort_order ?? 0
                                                });
                                                let existing = plan.features;
                                                if (typeof existing === 'string') {
                                                    try { existing = JSON.parse(existing); } catch { existing = []; }
                                                }
                                                if (!Array.isArray(existing)) existing = [];
                                                setFeaturesText(existing.join('\n'));
                                                setShowEditPlanModal(true);
                                            }}
                                            className="btn-secondary flex-1 flex items-center justify-center gap-2"
                                        >
                                            <Edit className="w-4 h-4" />
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-white">Recent Transactions</h2>
                        
                        <div className="glass-card">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-800/50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Transaction ID
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Customer
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Cardholder
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Amount
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Date
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-700">
                                        {transactions.map((transaction) => (
                                            <tr key={transaction.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                                    {transaction.id}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    <div>{transaction.customer_email || 'N/A'}</div>
                                                    {transaction.customer_name && (
                                                        <div className="text-xs text-gray-500">{transaction.customer_name}</div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    {transaction.cardholder_name || '—'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                                    {formatCurrency(transaction.amount, transaction.currency)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        transaction.status === 'completed' 
                                                            ? 'bg-green-600/20 text-green-400'
                                                            : transaction.status === 'failed'
                                                            ? 'bg-red-600/20 text-red-400'
                                                            : 'bg-yellow-600/20 text-yellow-400'
                                                    }`}>
                                                        {transaction.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    {formatDate(transaction.created_at)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    <button 
                                                        onClick={() => handleViewTransactionDetails(transaction)}
                                                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Subscribers Tab */}
                {activeTab === 'subscribers' && (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-white">Active Subscribers</h2>
                        
                        <div className="glass-card">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-800/50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Customer
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Plan
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Next Billing
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-700">
                                        {subscribers.map((subscriber) => (
                                            <tr key={subscriber.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                                    {subscriber.customer_email}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    {subscriber.plan_name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        subscriber.status?.toUpperCase() === 'ACTIVE'
                                                            ? 'bg-green-600/20 text-green-400'
                                                            : subscriber.status?.toUpperCase() === 'CANCELED'
                                                            ? 'bg-red-600/20 text-red-400'
                                                            : 'bg-yellow-600/20 text-yellow-400'
                                                    }`}>
                                                        {subscriber.status ?? '—'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    {formatDate(subscriber.next_billing_date)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    <button
                                                        onClick={() => { setSelectedSubscriber(subscriber); setShowSubscriberModal(true); }}
                                                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Create Plan Modal */}
            {showCreatePlanModal && (
                <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
                    <div className="glass-card max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white">Create Subscription Plan</h3>
                            <button
                                onClick={() => { setShowCreatePlanModal(false); setFeaturesText(''); }}
                                className="text-gray-400 hover:text-white"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Form */}
                            <form onSubmit={handleCreatePlan} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Plan Name</label>
                                    <input
                                        type="text"
                                        value={planFormData.name}
                                        onChange={(e) => setPlanFormData({ ...planFormData, name: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                                    <textarea
                                        value={planFormData.description}
                                        onChange={(e) => setPlanFormData({ ...planFormData, description: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        rows="2"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Features (one per line)</label>
                                    <textarea
                                        value={featuresText}
                                        onChange={(e) => setFeaturesText(e.target.value)}
                                        placeholder={"Unlimited uploads\nPriority support\nHD audio quality"}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                        rows="5"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Price (cents)</label>
                                        <input
                                            type="number"
                                            value={planFormData.price}
                                            onChange={(e) => setPlanFormData({ ...planFormData, price: e.target.value })}
                                            placeholder="e.g. 999 = $9.99"
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Billing Interval</label>
                                        <select
                                            value={planFormData.billing_interval}
                                            onChange={(e) => setPlanFormData({ ...planFormData, billing_interval: e.target.value })}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="MONTHLY">Monthly</option>
                                            <option value="YEARLY">Yearly</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Badge Text</label>
                                        <input
                                            type="text"
                                            value={planFormData.badge_text}
                                            onChange={(e) => setPlanFormData({ ...planFormData, badge_text: e.target.value })}
                                            placeholder="e.g. Popular, Best Value"
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Sort Order</label>
                                        <input
                                            type="number"
                                            value={planFormData.sort_order}
                                            onChange={(e) => setPlanFormData({ ...planFormData, sort_order: e.target.value })}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
                                    <input
                                        type="checkbox"
                                        id="create-is-popular"
                                        checked={planFormData.is_popular}
                                        onChange={(e) => setPlanFormData({ ...planFormData, is_popular: e.target.checked })}
                                        className="w-4 h-4 rounded"
                                    />
                                    <label htmlFor="create-is-popular" className="text-gray-300 text-sm">
                                        Mark as popular
                                    </label>
                                </div>

                                <div className="flex justify-end gap-3 pt-6 border-t border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => { setShowCreatePlanModal(false); setFeaturesText(''); }}
                                        className="btn-secondary"
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primary">
                                        Create Plan
                                    </button>
                                </div>
                            </form>

                            {/* Live Preview */}
                            <div className="lg:sticky lg:top-0">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Live Preview</p>
                                <PlanPreviewCard formData={planFormData} featuresText={featuresText} />
                                <p className="text-xs text-gray-600 mt-3 text-center">Updates as you type</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Plan Modal */}
            {showEditPlanModal && selectedPlan && (
                <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
                    <div className="glass-card max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white">Edit Subscription Plan</h3>
                            <button
                                onClick={() => { setShowEditPlanModal(false); setSelectedPlan(null); setFeaturesText(''); }}
                                className="text-gray-400 hover:text-white"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Form */}
                            <form onSubmit={handleEditPlan} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Plan Name</label>
                                    <input
                                        type="text"
                                        value={planFormData.name}
                                        onChange={(e) => setPlanFormData({ ...planFormData, name: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                                    <textarea
                                        value={planFormData.description}
                                        onChange={(e) => setPlanFormData({ ...planFormData, description: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        rows="2"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Features (one per line)</label>
                                    <textarea
                                        value={featuresText}
                                        onChange={(e) => setFeaturesText(e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                        rows="5"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Price (cents)</label>
                                        <input
                                            type="number"
                                            value={planFormData.price}
                                            onChange={(e) => setPlanFormData({ ...planFormData, price: e.target.value })}
                                            placeholder="e.g. 999 = $9.99"
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Billing Interval</label>
                                        <select
                                            value={planFormData.billing_interval}
                                            onChange={(e) => setPlanFormData({ ...planFormData, billing_interval: e.target.value })}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="MONTHLY">Monthly</option>
                                            <option value="YEARLY">Yearly</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Badge Text</label>
                                        <input
                                            type="text"
                                            value={planFormData.badge_text || ''}
                                            onChange={(e) => setPlanFormData({ ...planFormData, badge_text: e.target.value })}
                                            placeholder="e.g. Popular, Best Value"
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Sort Order</label>
                                        <input
                                            type="number"
                                            value={planFormData.sort_order ?? 0}
                                            onChange={(e) => setPlanFormData({ ...planFormData, sort_order: e.target.value })}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
                                    <input
                                        type="checkbox"
                                        id="edit-is-popular"
                                        checked={!!planFormData.is_popular}
                                        onChange={(e) => setPlanFormData({ ...planFormData, is_popular: e.target.checked })}
                                        className="w-4 h-4 rounded"
                                    />
                                    <label htmlFor="edit-is-popular" className="text-gray-300 text-sm">
                                        Mark as popular
                                    </label>
                                </div>

                                <div className="flex justify-end gap-3 pt-6 border-t border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => { setShowEditPlanModal(false); setSelectedPlan(null); setFeaturesText(''); }}
                                        className="btn-secondary"
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primary">
                                        Save Changes
                                    </button>
                                </div>
                            </form>

                            {/* Live Preview */}
                            <div className="lg:sticky lg:top-0">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Live Preview</p>
                                <PlanPreviewCard formData={planFormData} featuresText={featuresText} />
                                <p className="text-xs text-gray-600 mt-3 text-center">Updates as you type</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Transaction Details Modal */}
            {showTransactionModal && selectedTransaction && (
                <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
                    <div className="glass-card max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-white">Transaction Details</h3>
                            <button
                                onClick={() => {
                                    setShowTransactionModal(false);
                                    setSelectedTransaction(null);
                                    setTransactionDetails(null);
                                }}
                                className="text-gray-400 hover:text-white"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {loadingTransactionDetails ? (
                            <div className="text-center py-8">
                                <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                <p className="text-gray-400">Loading transaction details...</p>
                            </div>
                        ) : transactionDetails ? (
                            <div className="space-y-6">
                                {/* Payment Information */}
                                <div>
                                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-blue-400" />
                                        Payment Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-800/50 rounded-lg">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Payment ID</label>
                                            <p className="text-white font-mono">{transactionDetails.id}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Square Payment ID</label>
                                            <p className="text-white font-mono">{transactionDetails.square_payment_id}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Amount</label>
                                            <p className="text-white text-lg font-semibold">
                                                {formatCurrency(transactionDetails.amount, transactionDetails.currency)}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Status</label>
                                            <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${
                                                transactionDetails.status === 'COMPLETED' 
                                                    ? 'bg-green-600/20 text-green-400'
                                                    : transactionDetails.status === 'FAILED'
                                                    ? 'bg-red-600/20 text-red-400'
                                                    : 'bg-yellow-600/20 text-yellow-400'
                                            }`}>
                                                {transactionDetails.status}
                                            </span>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Date</label>
                                            <p className="text-white">{formatDate(transactionDetails.created_at)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Processed At</label>
                                            <p className="text-white">
                                                {transactionDetails.processed_at ? formatDateTime(transactionDetails.processed_at) : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Customer Information */}
                                <div>
                                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                        <Users className="w-5 h-5 text-green-400" />
                                        Customer Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-800/50 rounded-lg">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Email</label>
                                            <p className="text-white">{transactionDetails.customer_email}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400">Name</label>
                                            <p className="text-white">
                                                {transactionDetails.customer_name || transactionDetails.username || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Method */}
                                {(transactionDetails.card_brand || transactionDetails.last_4) && (
                                    <div>
                                        <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                            <CreditCard className="w-5 h-5 text-purple-400" />
                                            Payment Method
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-800/50 rounded-lg">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400">Card Brand</label>
                                                <p className="text-white">{transactionDetails.card_brand || transactionDetails.payment_method}</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400">Last 4 Digits</label>
                                                <p className="text-white">****{transactionDetails.last_4 || 'N/A'}</p>
                                            </div>
                                            {transactionDetails.exp_month && transactionDetails.exp_year && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-400">Expiry</label>
                                                    <p className="text-white">
                                                        {String(transactionDetails.exp_month).padStart(2, '0')}/{transactionDetails.exp_year}
                                                    </p>
                                                </div>
                                            )}
                                            {transactionDetails.cardholder_name && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-400">Cardholder Name</label>
                                                    <p className="text-white">{transactionDetails.cardholder_name}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Plan Information */}
                                {transactionDetails.plan_name && (
                                    <div>
                                        <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                            <Activity className="w-5 h-5 text-yellow-400" />
                                            Subscription Plan
                                        </h4>
                                        <div className="p-4 bg-gray-800/50 rounded-lg">
                                            <div className="mb-2">
                                                <label className="block text-sm font-medium text-gray-400">Plan Name</label>
                                                <p className="text-white font-semibold">{transactionDetails.plan_name}</p>
                                            </div>
                                            {transactionDetails.plan_description && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-400">Description</label>
                                                    <p className="text-gray-300">{transactionDetails.plan_description}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Receipt Link */}
                                {transactionDetails.receipt_url && (
                                    <div>
                                        <h4 className="text-lg font-semibold text-white mb-3">Receipt</h4>
                                        <div className="p-4 bg-gray-800/50 rounded-lg">
                                            <a 
                                                href={transactionDetails.receipt_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-400 hover:text-blue-300 underline flex items-center gap-2"
                                            >
                                                View Receipt
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </a>
                                        </div>
                                    </div>
                                )}

                                {/* Error Information */}
                                {transactionDetails.failure_reason && (
                                    <div>
                                        <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center gap-2">
                                            <AlertCircle className="w-5 h-5" />
                                            Failure Reason
                                        </h4>
                                        <div className="p-4 bg-red-600/10 border border-red-600/30 rounded-lg">
                                            <p className="text-red-300">{transactionDetails.failure_reason}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                                <p className="text-red-400">Failed to load transaction details</p>
                            </div>
                        )}

                        <div className="flex justify-end pt-6 border-t border-gray-700 mt-6">
                            <button
                                onClick={() => {
                                    setShowTransactionModal(false);
                                    setSelectedTransaction(null);
                                    setTransactionDetails(null);
                                }}
                                className="btn-secondary"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Subscriber Details Modal */}
            {showSubscriberModal && selectedSubscriber && (
                <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
                    <div className="glass-card max-w-lg w-full p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-white">Subscriber Details</h3>
                            <button
                                onClick={() => { setShowSubscriberModal(false); setSelectedSubscriber(null); }}
                                className="text-gray-400 hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                                    <p className="text-sm text-white">{selectedSubscriber.customer_email}</p>
                                    {selectedSubscriber.display_name && (
                                        <p className="text-xs text-gray-400">{selectedSubscriber.display_name} (@{selectedSubscriber.username})</p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Plan</p>
                                    <p className="text-sm text-white">{selectedSubscriber.plan_name}</p>
                                    <p className="text-xs text-gray-400">
                                        {fmtCurrency(selectedSubscriber.price)} / {selectedSubscriber.billing_interval === 'YEARLY' ? 'year' : 'month'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                                    <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                                        selectedSubscriber.status?.toUpperCase() === 'ACTIVE'
                                            ? 'bg-green-600/20 text-green-400'
                                            : selectedSubscriber.status?.toUpperCase() === 'CANCELED'
                                            ? 'bg-red-600/20 text-red-400'
                                            : 'bg-yellow-600/20 text-yellow-400'
                                    }`}>
                                        {selectedSubscriber.status ?? '—'}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Subscribed</p>
                                    <p className="text-sm text-gray-300">{formatDate(selectedSubscriber.subscribed_at)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Current Period</p>
                                    <p className="text-sm text-gray-300">
                                        {formatDate(selectedSubscriber.current_period_start)} — {formatDate(selectedSubscriber.current_period_end)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Next Billing</p>
                                    <p className="text-sm text-gray-300">{formatDate(selectedSubscriber.next_billing_date)}</p>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-gray-700">
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Square Subscription ID</p>
                                <p className="text-xs text-gray-400 font-mono break-all">{selectedSubscriber.square_subscription_id ?? '—'}</p>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => { setShowSubscriberModal(false); setSelectedSubscriber(null); }}
                                className="btn-secondary"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentsManagement;